import { useState, useRef, useEffect } from 'react';
import DocumentActionConfirmToastContent from '../components/DocumentActionConfirmToastContent.jsx';
import {
    useUpdateVehicleAcceptanceFormMutation,
    useDeleteVehicleAcceptanceFormMutation,
    useApprovementVehicleAcceptanceFormMutation,
    useUploadDocumentMutation,
    useLazyGetDocumentsByIdQuery,
    useDeleteDocumentMutation
} from '../../../../../api/endpoints/vehicleAcceptenceForm';

import { useLazyDownloadDocumentByKeyQuery } from '../../../../../api/endpoints/documentContent';
import DocumentDownloadService from '../../../../../utils/documentDownloadService';
import { formatDateTimeForApi } from '../../../../../utils/dateUtils';
import { VAFStatus } from '../constants/vafStatus.js';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { createVAFFieldHandlers } from './useVAFFormFieldHandlers';

export const useEditVehicleAcceptanceForm = (refetchList, dialogToastRef) => {
    const { th } = useLanguage();

    // Toast ref for notifications - YENİ: Dialog'daki toast'u kullan
    const localToastRef = useRef(null);
    const toast = dialogToastRef || localToastRef;

    // DocumentDownloadService instance'ı oluştur
    const [downloadService] = useState(() => new DocumentDownloadService(toast));

    // State tanımlamaları
    const [updateDialogVisible, setUpdateDialogVisible] = useState(false);
    const [selectedVehicleAcceptanceForm, setSelectedVehicleAcceptanceForm] = useState(null);
    const [editFormData, setEditFormData] = useState({
        chassisNo: '',
        vehicleAcceptDate: null,
        vehicleAcceptKm: null,
        description: '',
        status: null,
        returnMessage: ''
    });
    const [originalFormData, setOriginalFormData] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [updatedFiles, setUpdatedFiles] = useState([]);

    // YENİ: Dosya işlemleri için flag
    const [hasDocumentOperations, setHasDocumentOperations] = useState(false);
    // YENİ: Onay bekleme durumu
    const [isWaitingForDocumentConfirmation, setIsWaitingForDocumentConfirmation] = useState(false);

    // Yüklenme durumları (loading states)
    const [isUpdatingLoading, setIsUpdatingLoading] = useState(false);
    const [isSendingForApprovalLoading, setIsSendingForApprovalLoading] = useState(false);
    const [isReturningLoading, setIsReturningLoading] = useState(false);
    const [isApprovingLoading, setIsApprovingLoading] = useState(false);
    const [isDeletingLoading, setIsDeletingLoading] = useState(false);

    // DÜZELTME: Dosya işlemleri için loading state'ler - manuel yönetim
    const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
    const [isDeletingDocument, setIsDeletingDocument] = useState(false);
    const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);

    // Mutation ve Query hook'ları
    const [updateVehicleAcceptanceFormMutation] = useUpdateVehicleAcceptanceFormMutation();    
    const [deleteVehicleAcceptanceFormMutation] = useDeleteVehicleAcceptanceFormMutation();
    const [approvementVehicleAcceptanceFormMutation] = useApprovementVehicleAcceptanceFormMutation();
    const [uploadDocumentMutation] = useUploadDocumentMutation();
    const [deleteDocumentMutation] = useDeleteDocumentMutation();

    // DocumentContent API için hook - fileKey ile indirme
    const [triggerDownloadByKey] = useLazyDownloadDocumentByKeyQuery();

    const [
        triggerGetDocuments,
        { data: documents, isLoading: isLoadingDocuments, isUninitialized, reset }
    ] = useLazyGetDocumentsByIdQuery();

    const handleEdit = (vehicleAcceptanceForm) => {
        setSelectedVehicleAcceptanceForm(vehicleAcceptanceForm);
        const formData = {
            chassisNo: vehicleAcceptanceForm.chassisNo,
            vehicleAcceptDate: vehicleAcceptanceForm.vehicleAcceptDate ? new Date(vehicleAcceptanceForm.vehicleAcceptDate) : null,
            vehicleAcceptKm: vehicleAcceptanceForm.vehicleAcceptKm,
            description: vehicleAcceptanceForm.description,
            status: vehicleAcceptanceForm.status,
            returnMessage: vehicleAcceptanceForm.returnMessage || ''
        };
        setEditFormData(formData);
        setOriginalFormData({ ...formData });
        if (vehicleAcceptanceForm && vehicleAcceptanceForm.id) {
            triggerGetDocuments(vehicleAcceptanceForm.id);
        }
        setUpdateDialogVisible(true);
        setSubmitted(false);
        setUpdatedFiles([]);
        setHasDocumentOperations(false); 
    };

    const hideUpdateDialog = () => {
        setUpdateDialogVisible(false);
        setSelectedVehicleAcceptanceForm(null);
        setSubmitted(false);
        setUpdatedFiles([]);
        setEditFormData({ chassisNo: '', vehicleAcceptDate: null, vehicleAcceptKm: null, description: '', status: null, returnMessage: '' });
        setOriginalFormData(null);
        setHasDocumentOperations(false);
        setIsDownloadingDocument(false);
        setIsDeletingDocument(false);
        setIsUpdatingDocument(false);
        setIsWaitingForDocumentConfirmation(false);
        reset();
    };

    const { onInputChange: onEditInputChange, onNumberChange: onEditNumberChange, onDateChange: onEditDateChange, onDropdownChange: onEditDropdownChange } =
        createVAFFieldHandlers(setEditFormData);

    const onEditFilesChange = (files) => {
        setUpdatedFiles(files);
    };

    const onRemoveFile = (indexToRemove) => {
        setUpdatedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    };

    const commonDataFormat = (formData, originalRecord) => ({
        id: originalRecord.id,
        chassisNo: formData.chassisNo,
        vehicleAcceptDate: formatDateTimeForApi(formData.vehicleAcceptDate),
        vehicleAcceptKm: formData.vehicleAcceptKm,
        description: formData.description,
        customerName: originalRecord.customerName,
        isActive: originalRecord.isActive !== undefined ? originalRecord.isActive : true,
    });

    const uploadFiles = async () => {
        if (updatedFiles.length > 0) {
            for (const file of updatedFiles) {
                await uploadDocumentMutation({
                    vehicleAcceptanceFormId: selectedVehicleAcceptanceForm.id,
                    file: file,
                    description: editFormData.description || file.name,
                }).unwrap();
            }
            if (selectedVehicleAcceptanceForm && selectedVehicleAcceptanceForm.id) {
                triggerGetDocuments(selectedVehicleAcceptanceForm.id);
            }
            setUpdatedFiles([]);
        }
    };

    const hasFormDataChanged = () => {
        if (!originalFormData) return false;

        let datesEqual = true;
        if (originalFormData.vehicleAcceptDate || editFormData.vehicleAcceptDate) {
            const originalDate = originalFormData.vehicleAcceptDate ?
                new Date(originalFormData.vehicleAcceptDate).toDateString() : null;
            const currentDate = editFormData.vehicleAcceptDate ?
                new Date(editFormData.vehicleAcceptDate).toDateString() : null;
            datesEqual = originalDate === currentDate;
        }

        const chassisChanged = originalFormData.chassisNo !== editFormData.chassisNo;
        const kmChanged = originalFormData.vehicleAcceptKm !== editFormData.vehicleAcceptKm;
        const descriptionChanged = (originalFormData.description || '') !== (editFormData.description || '');
        const returnMessageChanged = (originalFormData.returnMessage || '') !== (editFormData.returnMessage || '');

        const hasChanged = chassisChanged || !datesEqual || kmChanged || descriptionChanged || returnMessageChanged;

        return hasChanged;
    };

    const handleUpdate = async (event) => {
        if (event) {
            event.preventDefault();
        }
        setSubmitted(true);
        const currentFormStatus = selectedVehicleAcceptanceForm?.status;
        if (!selectedVehicleAcceptanceForm) return;

        if (currentFormStatus === VAFStatus.DRAFT || currentFormStatus === VAFStatus.RETURNED) {
            if (!editFormData.chassisNo || !editFormData.vehicleAcceptDate || editFormData.vehicleAcceptKm === null) {
                setIsUpdatingLoading(false);
                return;
            }
        }

        const formChanged = hasFormDataChanged();
        const hasNewFiles = updatedFiles.length > 0;

        if (hasDocumentOperations && !formChanged && !hasNewFiles) {
            if (toast.current) {
                toast.current.show({
                    severity: 'info',
                    summary: th('common.info'),
                    detail: th('warranty.vehicleAcceptanceForm.messages.fileChangesAutoSaved'),
                    life: 4000
                });
            }
            return;
        }

        if (!formChanged && !hasNewFiles && !hasDocumentOperations) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: th('common.warning'),
                    detail: th('warranty.vehicleAcceptanceForm.messages.noChangesWarning'),
                    life: 3000
                });
            } else {
                console.error('Toast ref is null - fallback to alert');
                alert(th('warranty.vehicleAcceptanceForm.messages.noChangesWarning'));
            }
            setIsUpdatingLoading(false);
            return;
        }

        setIsUpdatingLoading(true);
        try {
            if (formChanged) {
                const dataPayload = {
                    ...commonDataFormat(editFormData, selectedVehicleAcceptanceForm),
                    status: currentFormStatus,
                };
                await updateVehicleAcceptanceFormMutation({ data: dataPayload }).unwrap();   
            }

            if (hasNewFiles) {
                await uploadFiles();
            }

            setHasDocumentOperations(false);

            if (refetchList) refetchList();

            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('warranty.vehicleAcceptanceForm.messages.success'),
                life: 3000
            });

            hideUpdateDialog();
        } catch (error) {
            console.error("Update failed:", error);
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('warranty.vehicleAcceptanceForm.messages.updateFailed', {
                    detail: error.data?.message || error.message || th('common.unknownError')
                }),
                life: 5000
            });
        } finally {
            setIsUpdatingLoading(false);
        }
    };

    const handleSubmitForApproval = async () => {
        setSubmitted(true);
        if (!selectedVehicleAcceptanceForm || !editFormData.chassisNo || !editFormData.vehicleAcceptDate || !editFormData.vehicleAcceptKm) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('warranty.vehicleAcceptanceForm.messages.requiredFieldsMissing'),
                life: 4000
            });
            return;
        }

        const hasExistingFiles = documents && documents.length > 0;
        const hasNewFiles = updatedFiles.length > 0;

        if (!hasExistingFiles && !hasNewFiles) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('warranty.vehicleAcceptanceForm.messages.atLeastOneFileRequired'),
                life: 4000
            });
            return;
        }

        setIsSendingForApprovalLoading(true);
        try {
            const formChanged = hasFormDataChanged();

            if (formChanged) {
                const dataToUpdate = {
                    ...commonDataFormat(editFormData, selectedVehicleAcceptanceForm),
                    status: selectedVehicleAcceptanceForm.status,
                };
                await    updateVehicleAcceptanceFormMutation({ data: dataToUpdate }).unwrap();
            }

            if (hasNewFiles) {
                await uploadFiles();
            }

            const approvalPayload = {
                Id: selectedVehicleAcceptanceForm.id,
                Status: VAFStatus.SENT_FOR_APPROVAL,
                ReturnMessage: null
            };
            await approvementVehicleAcceptanceFormMutation(approvalPayload).unwrap();

            if (refetchList) refetchList();
            hideUpdateDialog();

            toast.current?.show({
                severity: 'success',
                summary: th('common.success'),
                detail: th('warranty.vehicleAcceptanceForm.messages.submittedForApproval'),
                life: 3000
            });
        } catch (error) {
            console.error("Onaya gönderme işlemi başarısız oldu:", error);
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('warranty.vehicleAcceptanceForm.messages.operationFailed', {
                    detail: error.data?.message || th('common.unknownError')
                }),
                life: 5000
            });
        } finally {
            setIsSendingForApprovalLoading(false);
        }
    };

    const processApprovementAction = async (newStatus, setLoadingState, message) => {
        setSubmitted(true);
        if (!selectedVehicleAcceptanceForm) {
            console.error("No selected form to process.");
            setLoadingState(false);
            return;
        }
        if (newStatus === VAFStatus.RETURNED && !message) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('warranty.vehicleAcceptanceForm.validation.returnReasonRequired'),
                life: 4000
            });
            setLoadingState(false);
            return;
        }
        setLoadingState(true);
        try {
            const payload = {
                Id: selectedVehicleAcceptanceForm.id,
                Status: newStatus,
                ReturnMessage: message
            };
            await approvementVehicleAcceptanceFormMutation(payload).unwrap();
            if (refetchList) refetchList();
            hideUpdateDialog();
        } catch (error) {
            console.error(`Failed to update status to ${newStatus} via approvement:`, error);
        } finally {
            setLoadingState(false);
        }
    };

    const handleReturn = (message) => processApprovementAction(VAFStatus.RETURNED, setIsReturningLoading, message);
    const handleApprove = (message) => processApprovementAction(VAFStatus.APPROVED, setIsApprovingLoading, message);

    const handleDeleteAction = async () => {
        if (!selectedVehicleAcceptanceForm) return;
        if (window.confirm(th('warranty.vehicleAcceptanceForm.messages.deleteConfirm'))) {
            setIsDeletingLoading(true);
            try {
                await deleteVehicleAcceptanceFormMutation({ id: selectedVehicleAcceptanceForm.id }).unwrap();
                hideUpdateDialog();
                if (refetchList) refetchList();
            } catch (error) {
                console.error("Delete failed:", error);
            } finally {
                setIsDeletingLoading(false);
            }
        }
    };

    const handleDeleteDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        setIsWaitingForDocumentConfirmation(true);

        // Onay dialogu göster - closable: false ile çarpı butonunu kaldır
        if (toast.current) {
            toast.current.show({
                severity: 'warn',
                summary: th('warranty.vehicleAcceptanceForm.messages.confirmationRequired'),
                detail: th('warranty.vehicleAcceptanceForm.messages.documentDeleteConfirm', { fileName: documentData.fileName }),
                sticky: true,
                closable: false,
                content: (props) => (
                    <DocumentActionConfirmToastContent
                        summary={props.message.summary}
                        detail={props.message.detail}
                        cancelLabel={th('common.cancel')}
                        confirmLabel={th('common.delete')}
                        confirmSeverity="danger"
                        onCancel={() => {
                            toast.current.clear();
                            setIsWaitingForDocumentConfirmation(false);
                        }}
                        onConfirm={async () => {
                            toast.current.clear();
                            setIsWaitingForDocumentConfirmation(false);
                            setIsDeletingDocument(true);
                            try {
                                await deleteDocumentMutation({
                                    Id: selectedVehicleAcceptanceForm.id,
                                    DocumentId: documentData.id
                                }).unwrap();

                                setHasDocumentOperations(true);

                                if (selectedVehicleAcceptanceForm && selectedVehicleAcceptanceForm.id) {
                                    triggerGetDocuments(selectedVehicleAcceptanceForm.id);
                                }

                                toast.current?.show({
                                    severity: 'success',
                                    summary: th('common.success'),
                                    detail: th('warranty.vehicleAcceptanceForm.messages.documentDeleted'),
                                    life: 3000
                                });
                            } catch (error) {
                                console.error("Belge silme işlemi başarısız oldu:", error);
                                const errorMessage = error?.data?.message || error?.message || th('common.unknownError');
                                toast.current?.show({
                                    severity: 'error',
                                    summary: th('common.error'),
                                    detail: th('warranty.vehicleAcceptanceForm.messages.documentDeleteFailed', { detail: errorMessage }),
                                    life: 4000
                                });
                            } finally {
                                setIsDeletingDocument(false);
                            }
                        }}
                    />
                )
            });
        }
    };

    const handleUpdateDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!selectedVehicleAcceptanceForm || !documentData.newFile) {
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('warranty.vehicleAcceptanceForm.messages.noFileSelectedForUpdate'),
                life: 3000
            });
            return;
        }

        // Onay bekleme durumunu aktif et
        setIsWaitingForDocumentConfirmation(true);

        // Güncelleme onayı - closable: false ile çarpı butonunu kaldır
        if (toast.current) {
            toast.current.show({
                severity: 'warn',
                summary: th('warranty.vehicleAcceptanceForm.messages.confirmationRequired'),
                detail: th('warranty.vehicleAcceptanceForm.messages.documentUpdateConfirm', { fileName: documentData.fileName }),
                sticky: true,
                closable: false,
                content: (props) => (
                    <DocumentActionConfirmToastContent
                        summary={props.message.summary}
                        detail={props.message.detail}
                        cancelLabel={th('common.cancel')}
                        confirmLabel={th('common.update')}
                        confirmSeverity="warning"
                        onCancel={() => {
                            toast.current.clear();
                            setIsWaitingForDocumentConfirmation(false);
                        }}
                        onConfirm={async () => {
                            toast.current.clear();
                            setIsWaitingForDocumentConfirmation(false);
                            setIsUpdatingDocument(true);
                            try {
                                // Önce eski dosyayı sil
                                await deleteDocumentMutation({
                                    Id: selectedVehicleAcceptanceForm.id,
                                    DocumentId: documentData.id
                                }).unwrap();

                                // Yeni dosyayı yükle
                                await uploadDocumentMutation({
                                    vehicleAcceptanceFormId: selectedVehicleAcceptanceForm.id,
                                    file: documentData.newFile,
                                    description: documentData.newFile.name,
                                }).unwrap();

                                setHasDocumentOperations(true);

                                // Belge listesini yenile
                                if (selectedVehicleAcceptanceForm && selectedVehicleAcceptanceForm.id) {
                                    triggerGetDocuments(selectedVehicleAcceptanceForm.id);
                                }

                                toast.current?.show({
                                    severity: 'success',
                                    summary: th('common.success'),
                                    detail: th('warranty.vehicleAcceptanceForm.messages.documentUpdated'),
                                    life: 3000
                                });
                            } catch (error) {
                                console.error("Belge güncelleme işlemi başarısız oldu:", error);
                                const errorMessage = error?.data?.message || error?.message || th('common.unknownError');
                                toast.current?.show({
                                    severity: 'error',
                                    summary: th('common.error'),
                                    detail: th('warranty.vehicleAcceptanceForm.messages.documentUpdateFailed', { detail: errorMessage }),
                                    life: 4000
                                });
                            } finally {
                                setIsUpdatingDocument(false);
                            }
                        }}
                    />
                )
            });
        }
    };

    const handleDownloadDocument = async (documentData, event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        setIsDownloadingDocument(true);

        toast.current?.show({
            severity: 'info',
            summary: th('common.downloading'),
            detail: th('warranty.vehicleAcceptanceForm.messages.downloadingFile', { fileName: documentData.fileName }),
            life: 3000
        });

        try {
            if (downloadService.toastRef !== toast) {
                downloadService.toastRef = toast;
            }

            // İndirme işlemini başlat
            await downloadService.downloadByKey(
                triggerDownloadByKey,
                documentData.fileKey,
                documentData,
                {
                    successMessage: th('warranty.vehicleAcceptanceForm.download.success'),
                    errorMessage: th('warranty.vehicleAcceptanceForm.download.error')
                }
            );
        } catch (error) {
            console.error("Download failed:", error);
            toast.current?.show({
                severity: 'error',
                summary: th('common.error'),
                detail: th('warranty.vehicleAcceptanceForm.messages.documentDownloadFailed', {
                    detail: error.message || th('common.unknownError')
                }),
                life: 4000
            });
        } finally {
            setIsDownloadingDocument(false);
        }
    };

    const setToastCallback = (toastRef) => {
        if (toastRef && toastRef.current) {
            downloadService.toastRef = toastRef;
        }
    };

    return {
        updateDialogVisible,
        selectedVehicleAcceptanceForm,
        editFormData,
        onEditInputChange,
        onEditNumberChange,
        onEditDateChange,
        onEditDropdownChange,
        updatedFiles,
        onEditFilesChange,
        onRemoveFile,
        submitted,
        handleEdit,
        hideUpdateDialog,
        handleUpdate,
        isUpdating: isUpdatingLoading,
        onSendForApproval: handleSubmitForApproval,
        isSendingForApproval: isSendingForApprovalLoading,
        onReturn: handleReturn,
        isReturning: isReturningLoading,
        onApprove: handleApprove,
        isApproving: isApprovingLoading,
        onDelete: handleDeleteAction,
        isDeleting: isDeletingLoading,
        documents,
        isLoadingDocuments,
        onDeleteDocument: handleDeleteDocument,
        onDownloadDocument: handleDownloadDocument,
        onUpdateDocument: handleUpdateDocument,
        isDownloadingDocument, 
        isDeletingDocument,
        isUpdatingDocument,
        isWaitingForDocumentConfirmation,
        toast,
        setToastCallback,
        hasDocumentOperations,
        hasFormDataChanged,
    };
};